import { FieldGroup, Field, Label, Select, Input } from '../../../atoms'
import React from 'react'
import { words, capitalize } from 'lodash-es'
import { DirectorDetails } from '.'
import { nationalties } from './country-iso-codes'

const getNames = (name: string) => {
  const [, lastName, firstName] = name.match(/([^,]*), (\S*)/) || []
  return [firstName, capitalize(lastName)]
}

// formatName('SYED, Tariq Sajjad') => 'Tariq Syed'
const formatName = (name: string) => getNames(name).join(' ')

const getFirstName = (directorDetails: DirectorDetails) => getNames(directorDetails.name ?? '')[0]
const getLastName = (directorDetails: DirectorDetails) => getNames(directorDetails.name ?? '')[1]

export const toLabel = (field: string) => words(field).map(capitalize).join(' ')

const DirectorFinder = ({ directorDetails, businessDirectors, register, setValue, errors }) => {
  const DirectorFields = {
    title: {
      validation: {
        required: { value: true, message: 'Please enter your title' },
        maxLength: {
          value: 50,
          message: 'Maximum 50 character address',
        },
      },
    },
    firstName: {
      validation: {
        required: { value: true, message: 'Please enter your first name' },
        maxLength: {
          value: 50,
          message: 'Maximum 50 character address',
        },
      },
    },
    lastName: {
      validation: {
        required: { value: true, message: 'Please enter your last name' },
        maxLength: {
          value: 50,
          message: 'Maximum 50 character address',
        },
      },
    },
    dateOfBirth: {
      validation: {
        required: { value: true, message: 'Please enter your date of birth' },
      },
    },
    emailAddress: {
      validation: {
        required: { value: true, message: 'Please enter your email address' },
        pattern: {
          value: /^\w+([-+.']\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*$/,
          message: 'please enter a valid email address',
        },
      },
    },
    mobileNumber: {
      validation: {
        required: { value: true, message: 'Please enter your mobile number' },
        pattern: {
          value: /^07[0-9]{9}$/,
          message: 'please enter a valid mobile number',
        },
      },
    },
    nationality: {
      validation: {
        required: { value: true, message: 'Please enter your Nationality' },
      },
    },
  }
  const fields = Object.keys(DirectorFields)

  return (
    <Field className='SMEform__companyDetails'>
      <Label>Director details</Label>
      <div className='SMEform__input'>
        <Field>
          <Select
            name='director-details'
            value={directorDetails.name ?? 'default'}
            onChange={(e) => {
              const rawDirectorDetails = businessDirectors.find((d) => d.name === e.target.value)
              setValue('directorDetails', {
                ...rawDirectorDetails,
                name: rawDirectorDetails.name,
                firstName: getFirstName(rawDirectorDetails),
                lastName: getLastName(rawDirectorDetails),
                title: undefined,
              })
            }}
            options={[
              { value: 'default', text: 'Select' },
              ...businessDirectors.map(({ name }: { name: string }) => {
                return {
                  value: name,
                  text: formatName(name),
                }
              }),
            ]}
          />
        </Field>
        {directorDetails.firstName && (
          <div className='SMEform__directorDetails__detailsBox'>
            {fields.map((field, index) => {
              return field === 'title' ? (
                <Field key={index}>
                  <Label>{toLabel(field)}</Label>
                  <Select
                    name={field}
                    value={directorDetails[field] ?? 'default'}
                    onChange={(e) => {
                      setValue(`directorDetails.${field}`, e.target.value)
                    }}
                    options={[
                      { value: 'default', text: 'Select' },
                      { value: 'Mr', text: 'Mr' },
                      { value: 'Mrs', text: 'Mrs' },
                      { value: 'Miss', text: 'Miss' },
                      { value: 'Ms', text: 'Ms' },
                      { value: 'Dr', text: 'Dr' },
                    ]}
                    {...register(`directorDetails.${field}`, DirectorFields[field].validation)}
                  />
                  {errors?.directorDetails?.title && (
                    <div className='SMEform__error'>{errors?.directorDetails?.title.message}</div>
                  )}
                </Field>
              ) : field === 'dateOfBirth' ? (
                <Field key={index}>
                  <Label>Date of Birth</Label>
                  <FieldGroup>
                    <Input
                      placeholder='DD'
                      value=''
                      onChange={(e) => {
                        setValue(`directorDetails.dateOfBirthFields.day`, e.target.value)
                      }}
                      {...register(
                        'directorDetails.dateOfBirthFields.day',
                        DirectorFields[field].validation,
                      )}
                    />
                    <Input
                      placeholder='MM'
                      value=''
                      onChange={(e) => {
                        setValue(`directorDetails.dateOfBirthFields.month`, e.target.value)
                      }}
                      {...register(
                        'directorDetails.dateOfBirthFields.month',
                        DirectorFields[field].validation,
                      )}
                    />
                    <Input
                      placeholder='YYYY'
                      value=''
                      onChange={(e) => {
                        setValue(`directorDetails.dateOfBirthFields.year`, e.target.value)
                      }}
                      {...register(
                        'directorDetails.dateOfBirthFields.year',
                        DirectorFields[field].validation,
                      )}
                    />
                  </FieldGroup>
                  {errors?.directorDetails?.dateOfBirth && (
                    <div className='SMEform__error'>
                      {errors?.directorDetails?.dateOfBirth.message}
                    </div>
                  )}
                </Field>
              ) : field === 'nationality' ? (
                <Field key={index}>
                  <Label>{toLabel(field)}</Label>
                  <Select
                    name={field}
                    value={directorDetails[field] ?? 'default'}
                    onChange={(e) => {
                      setValue(`directorDetails.${field}`, e.target.value)
                    }}
                    options={[{ value: 'default', text: 'Select' }].concat(nationalties)}
                    {...register(`directorDetails.${field}`, DirectorFields[field].validation)}
                  />
                  {errors?.directorDetails?.[field] && (
                    <div className='SMEform__error'>{errors?.directorDetails?.[field].message}</div>
                  )}
                </Field>
              ) : (
                <Field key={index}>
                  <Label>{toLabel(field)}</Label>
                  <Input
                    name={field}
                    value={directorDetails[field] ?? ''}
                    onChange={(e) => {
                      setValue(`directorDetails.${field}`, e.target.value)
                    }}
                    type={'text'}
                    {...register(`directorDetails.${field}`, DirectorFields[field].validation)}
                  />
                  {errors?.directorDetails?.[field] && (
                    <div className='SMEform__error'>{errors?.directorDetails?.[field].message}</div>
                  )}
                </Field>
              )
            })}
          </div>
        )}
      </div>
    </Field>
  )
}

export default DirectorFinder
